import React, { useState } from 'react';
import {
    Input,
    Form,
    Modal,
    Switch,
    DatePicker,
    DatePickerProps,
    Button,
    Select,
    Tooltip,
    Spin,
    notification,
} from 'antd';
import { Campaign } from '../../../shared/types';
import { convertDateToFormat, handleFormChange, layoutEdit, validateMessages } from '../../../shared/Utils';
import { useCreateCampaignMutation } from '../../../GraphQL/hooks/useCreateCampaignMutation';
import { InfoCircleOutlined } from '@ant-design/icons';

const CampaignCreateModal = ({ isOpen, onRequestClose, updateList }) => {
    const [form] = Form.useForm();
    const [newCampaign, setNewCampaign] = useState<Campaign>();
    const [status, setStatus] = useState<boolean>(false);
    const [formError, setFormError] = useState(false);

    const { mutation, loading: creating } = useCreateCampaignMutation();

    const handleSwitchChange = (checked: boolean) => {
        setStatus(checked);
    };

    const handleSelectStartingDate = (date: any, dateString: any) => {
        setNewCampaign({ ...newCampaign, startingDate: convertDateToFormat(date, 'DD-MM-YYYY') });
    };

    const handleSelectEndDate = (date: any, dateString: any) => {
        setNewCampaign({ ...newCampaign, endDate: convertDateToFormat(date, 'DD-MM-YYYY') });
    };

    const handleCommit = async () => {
        try {
            const values = await form.validateFields();
            if (values) {
                mutation({ ...newCampaign, status: status })
                    .then(() => {
                        onRequestClose(false);
                        updateList();
                    })
                    .finally(() => {
                        notification.success({
                            message: 'Campaign updated successfully',
                            description: 'The campaign has been updated successfully.',
                        });
                    })
                    .catch((err) => {
                        notification.error({
                            message: 'Failed to update campaign',
                            description: 'An error occurred while updating the campaign.',
                        });

                        console.error('Failed to update campaign:', err);
                    });
            }
        } catch (errorInfo) {
            notification.error({
                message: 'Form validation failed',
                description: 'Please check the form for errors and try again.',
            });
            console.error('Form validation failed:', errorInfo);
        }
    };
    const allowedTargetAudiences = ['RIDER', 'DRIVER', 'ALL'];

    const handleCancel = () => {
        onRequestClose(false);
    };

    return (
        <Modal
            forceRender
            title="Add Campaign"
            open={isOpen}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="create" type="primary" onClick={handleCommit} disabled={formError}>
                    Create
                </Button>,
            ]}
        >
            <Spin spinning={creating}>
                <Form<Campaign>
                    {...layoutEdit}
                    form={form}
                    name="nest-messages"
                    autoComplete="off"
                    onFieldsChange={() => {
                        setFormError(handleFormChange(form));
                    }}
                    onValuesChange={(changedValues, allValues) => setNewCampaign(allValues)}
                    validateMessages={validateMessages}
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        style={{ display: 'inline-block', width: '100%' }}
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={4} placeholder="Enter campaign title" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                        style={{ display: 'inline-block', width: '100%' }}
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={4} placeholder="Enter campaign description" />
                    </Form.Item>
                    <Form.Item name="startingDate" label="Starting date" rules={[{ required: true }]}>
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectStartingDate} />
                    </Form.Item>
                    <Form.Item name="endDate" label="End date" rules={[{ required: true }]}>
                        <DatePicker format={'YYYY-MM-DD'} onChange={handleSelectEndDate} />
                    </Form.Item>
                    <Form.Item
                        name="targetAudience"
                        label="Audience"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 32 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Form.Item name="targetAudience" noStyle>
                                <Select placeholder="Select target audience" style={{ width: '50%' }}>
                                    {allowedTargetAudiences.map((type) => (
                                        <Select.Option key={type} value={type}>
                                            {type}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Tooltip title="Select the target audience for this campaign">
                                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                            </Tooltip>
                        </div>
                    </Form.Item>
                    <Form.Item name="status" label="Status" valuePropName="checked" rules={[{ required: false }]}>
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            checked={false}
                            onClick={(checked: boolean) => handleSwitchChange(checked)}
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default CampaignCreateModal;
